import { toast } from "react-hot-toast";

import { useAuth } from "../../auth/AuthContext";
import { Selector, type SelectorOption } from "../../components/ui";
import { useUpdateAgentRunVisibility, type AgentVisibility } from "../api/e2e";

import "./AgentVisibilityControl.css";

export const AGENT_VISIBILITY_OPTIONS: readonly SelectorOption<AgentVisibility>[] =
  [
    {
      value: "private",
      label: "Private",
      secondary: "Only you and administrators can access it.",
    },
    {
      value: "public",
      label: "Public",
      secondary: "Everyone in the workspace can access it.",
    },
  ];

function VisibilityIcon({ visibility }: { visibility: AgentVisibility }) {
  if (visibility === "public") {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="5.75" />
        <path d="M2.5 8h11M8 2.25c1.55 1.58 2.35 3.5 2.35 5.75S9.55 12.17 8 13.75C6.45 12.17 5.65 10.25 5.65 8S6.45 3.83 8 2.25Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3.25" y="7" width="9.5" height="6.5" rx="1.5" />
      <path d="M5.25 7V5.25a2.75 2.75 0 0 1 5.5 0V7" />
    </svg>
  );
}

function VisibilityValue({ visibility }: { visibility: AgentVisibility }) {
  return (
    <span className="agent-visibility-value">
      <VisibilityIcon visibility={visibility} />
      <span>{visibility === "public" ? "Public" : "Private"}</span>
    </span>
  );
}

export default function AgentVisibilityControl({
  runId,
  visibility,
  ownerSubject,
}: {
  runId: string;
  visibility: AgentVisibility;
  ownerSubject: string | null;
}) {
  const auth = useAuth();
  const updateVisibility = useUpdateAgentRunVisibility();
  const canChange =
    auth.canOperate ||
    (ownerSubject !== null && ownerSubject === auth.identity?.subject);

  if (!canChange) {
    return (
      <span
        className={`agent-visibility-readonly agent-visibility-readonly--${visibility}`}
        aria-label={`Run visibility: ${visibility}`}
      >
        <VisibilityValue visibility={visibility} />
      </span>
    );
  }

  return (
    <Selector
      options={AGENT_VISIBILITY_OPTIONS}
      value={visibility}
      onValueChange={(nextVisibility) => {
        if (nextVisibility === visibility) return;
        updateVisibility.mutate(
          { runId, visibility: nextVisibility },
          {
            onSuccess: () =>
              toast.success(
                `This run and any published model are now ${nextVisibility}.`,
              ),
            onError: (error) => toast.error(error.message),
          },
        );
      }}
      disabled={updateVisibility.isPending}
      ariaLabel="Run visibility"
      className={`agent-visibility-select agent-visibility-select--${visibility}`}
      portalMenu
      triggerContent={<VisibilityValue visibility={visibility} />}
    />
  );
}
